import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {normalize} from '@/lib/utils'
import {Hint} from '@/components/ui/hint'
import {tope} from '@/lib/campos-texto'

export type Opcion = {value: string; label: string}

/** Lista JSON del catálogo editada como chips creatables (§10). Con `opciones` no hay «crear»:
 * el catálogo ordena filas dentro de los conjuntos cerrados y no los amplía (invariante 7). */
export function ChipsField({
  id,
  label,
  hint,
  values,
  onChange,
  opciones,
  placeholder,
}: {
  id: string
  label: string
  hint?: string
  values: string[]
  onChange: (next: string[]) => void
  opciones?: Opcion[]
  placeholder?: string
}) {
  const {t} = useTranslation()
  const [query, setQuery] = useState('')

  const etiqueta = useMemo(() => {
    const porValor = new Map((opciones ?? []).map((o) => [o.value, o.label]))
    return (value: string) => porValor.get(value) ?? value
  }, [opciones])

  const candidatas = useMemo(() => {
    if (!opciones) return []
    const q = normalize(query)
    return opciones.filter((o) => !values.includes(o.value) && normalize(o.label).includes(q)).slice(0, 8)
  }, [opciones, query, values])

  function add(value: string) {
    const limpio = value.trim()
    if (!limpio || values.includes(limpio)) return
    onChange([...values, limpio])
    setQuery('')
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${id}-chips`}>
          {values.map((value) => (
            <span
              key={value}
              data-testid={`${id}-chip`}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground">
              <span className="min-w-0 truncate">{etiqueta(value)}</span>
              <button
                type="button"
                aria-label={t('adminCatalogo.form.quitarChip', {valor: etiqueta(value)})}
                data-testid={`${id}-quitar-${value}`}
                onClick={() => onChange(values.filter((v) => v !== value))}
                className="shrink-0 font-normal opacity-60 hover:opacity-100">
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        maxLength={tope('BUSCA')}
        id={id}
        data-testid={`${id}-input`}
        value={query}
        placeholder={placeholder}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          // Enter agrega dentro de un diálogo con submit aparte: sin preventDefault enviaría el formulario.
          if (e.key !== 'Enter' && e.key !== ',') return
          e.preventDefault()
          if (opciones) {
            if (candidatas.length > 0) add(candidatas[0].value)
            return
          }
          add(query)
        }}
      />
      {hint && <Hint>{hint}</Hint>}
      {opciones && candidatas.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" data-testid={`${id}-opciones`}>
          {candidatas.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                data-testid={`${id}-opcion-${o.value}`}
                onClick={() => add(o.value)}
                className="rounded-full border border-border px-2.5 py-0.5 text-xs hover:bg-secondary">
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
